// On-screen indicator pointing at the puck when it leaves the viewport.
//
// DOM overlay element: an SVG triangle whose geometry is built once; the
// per-frame update touches only the root's CSS transform (translate +
// rotate around the triangle's local origin) and display visibility.

import type { MatchSnapshot } from "@arcade-hockey/sim";
import type { PerspectiveCamera } from "three/webgpu";
import { type ScreenSize, simToScreenInto } from "../render/world-mapping.js";

const ARROW_SIZE = 18;
const SCREEN_SCRATCH = { x: 0, y: 0 };
// The svg is a square viewBox centered on the triangle's local origin so the
// root's rotation pivots at the triangle's center.
const SVG_HALF = ARROW_SIZE * 1.4;

const ROOT_STYLE: Partial<CSSStyleDeclaration> = {
  position: "absolute",
  left: "0",
  top: "0",
  display: "none",
  willChange: "transform",
};

export class OffscreenArrow {
  readonly root: HTMLDivElement;
  private visible = false;

  constructor() {
    this.root = document.createElement("div");
    Object.assign(this.root.style, ROOT_STYLE);

    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("width", `${SVG_HALF * 2}`);
    svg.setAttribute("height", `${SVG_HALF * 2}`);
    svg.setAttribute("viewBox", `${-SVG_HALF} ${-SVG_HALF} ${SVG_HALF * 2} ${SVG_HALF * 2}`);
    svg.style.position = "absolute";
    svg.style.left = `${-SVG_HALF}px`;
    svg.style.top = `${-SVG_HALF}px`;

    // Unit triangle: tip at (size, 0), base at (-0.4*size, ±0.6*size) —
    // matches the previous canvas geometry exactly.
    const tri = document.createElementNS("http://www.w3.org/2000/svg", "polygon");
    const b = ARROW_SIZE * 0.4;
    const h = ARROW_SIZE * 0.6;
    tri.setAttribute("points", `${ARROW_SIZE},0 ${-b},${h} ${-b},${-h}`);
    tri.setAttribute("fill", "#ffd84a");
    tri.setAttribute("fill-opacity", "0.95");
    tri.setAttribute("stroke", "#4a3a00");
    tri.setAttribute("stroke-width", "2");
    svg.appendChild(tri);
    this.root.appendChild(svg);
  }

  update(snap: MatchSnapshot, camera: PerspectiveCamera, screen: ScreenSize): void {
    simToScreenInto(SCREEN_SCRATCH, snap.puck.pos, 0, camera, screen);
    const margin = 40;
    const minX = margin;
    const maxX = screen.width - margin;
    const minY = margin;
    const maxY = screen.height - margin;

    const isOnScreen =
      SCREEN_SCRATCH.x >= minX &&
      SCREEN_SCRATCH.x <= maxX &&
      SCREEN_SCRATCH.y >= minY &&
      SCREEN_SCRATCH.y <= maxY;

    const shouldShow = !isOnScreen;
    if (shouldShow !== this.visible) {
      this.visible = shouldShow;
      this.root.style.display = shouldShow ? "block" : "none";
    }
    if (!shouldShow) return;

    const clampedX = Math.max(minX, Math.min(maxX, SCREEN_SCRATCH.x));
    const clampedY = Math.max(minY, Math.min(maxY, SCREEN_SCRATCH.y));
    const dx = SCREEN_SCRATCH.x - screen.width / 2;
    const dy = SCREEN_SCRATCH.y - screen.height / 2;
    const angle = Math.atan2(dy, dx);
    this.root.style.transform = `translate(${clampedX}px, ${clampedY}px) rotate(${angle}rad)`;
  }

  destroy(): void {
    this.root.remove();
  }
}
